/**
 * Single app routes: admin + user routes merged. No /admin prefix.
 * Permission-based access via ProtectedRoute; first path wins when duplicated.
 */
import {
  APP_ROUTES_ADD_FORMS,
  APP_ROUTES_ALLERGY,
  APP_ROUTES_ANC,
  APP_ROUTES_ANC_CARD,
  APP_ROUTES_ANC_FORM_TEMPLATES,
  APP_ROUTES_ANC_LABOUR,
  APP_ROUTES_ANC_PATIENT,
  APP_ROUTES_ANC_PN_VISIT,
  APP_ROUTES_ANC_VISIT,
  APP_ROUTES_OB_ORDER_SETS,
  APP_ROUTES_APP_ADMISSION,
  APP_ROUTES_APP_ASSIGNED,
  APP_ROUTES_APP_DISPENSE_INPATIENT,
  APP_ROUTES_APP_DISPENSE_OUTPATIENT,
  APP_ROUTES_APP_DOCUMENT_TEMPLATE,
  APP_ROUTES_APP_DOCUMENT_TEMPLATE_NEW,
  APP_ROUTES_APP_DRUG_INVENTORY,
  APP_ROUTES_APP_ENCOUNTERS,
  APP_ROUTES_BLOOD_BANK,
  APP_ROUTES_APP_INTAKE,
  APP_ROUTES_APP_INTAKE_NEW,
  APP_ROUTES_APP_MED_ORDER,
  APP_ROUTES_APP_NURSE_REQUISITIONS,
  APP_ROUTES_APP_PATIENT_CHART_INTAKE,
  APP_ROUTES_APP_PATIENT_CHART_INTAKE_ID,
  APP_ROUTES_APP_PATIENT_MAR,
  APP_ROUTES_APP_PHARMACIST_REQUISITIONS,
  APP_ROUTES_APP_PHARMACIST_REQUISITIONS_NEW,
  APP_ROUTES_APP_PHARMACY_WORKSPACE,
  APP_ROUTES_APP_PRODUCT_DETAILS,
  APP_ROUTES_APP_PHARMACY_REPORTS,
  APP_ROUTES_APP_REPORTS,
  APP_ROUTES_APP_REQUISITIONS_NEW,
  APP_ROUTES_APP_WAITLIST,
  APP_ROUTES_APP_WAITLIST_DOCTORS,
  APP_ROUTES_APP_WAITLIST_NURSES,
  APP_ROUTES_APP_WAITLIST_RECEPTIONISTS,
  APP_ROUTES_APPOINTMENT,
  APP_ROUTES_APPOINTMENT_SETUP,
  APP_ROUTES_ARCHIVES,
  APP_ROUTES_AUDIT,
  APP_ROUTES_BED,
  APP_ROUTES_BED_REQUEST,
  APP_ROUTES_BILLING,
  APP_ROUTES_CHARGE_ITEMS,
  APP_ROUTES_DEPARTMENT,
  APP_ROUTES_DEPARTMENT_ROOM,
  APP_ROUTES_DRUG,
  APP_ROUTES_FACILITY,
  APP_ROUTES_FORMS,
  APP_ROUTES_HMO,
  APP_ROUTES_HMO_PLAN,
  APP_ROUTES_HMO_PLAN_TARIFF,
  APP_ROUTES_IMAGING,
  APP_ROUTES_IMAGING_DOC_TEMPLATE,
  APP_ROUTES_IMAGING_DOC_TEMPLATE_NEW,
  APP_ROUTES_IMAGING_DOC_TEMPLATE_PREVIEW,
  APP_ROUTES_INVENTORY_EQUIPMENT,
  APP_ROUTES_INVENTORY_EQUIPMENT_DETAIL,
  APP_ROUTES_LAB_TECH,
  APP_ROUTES_LABORATORY,
  APP_ROUTES_LABORATORY_DOC_TEMPLATE,
  APP_ROUTES_LABORATORY_DOC_TEMPLATE_NEW,
  APP_ROUTES_LABORATORY_DOC_TEMPLATE_PREVIEW,
  APP_ROUTES_LOCATION,
  APP_ROUTES_PATIENT,
  APP_ROUTES_PATIENT_DIRECTORY,
  APP_ROUTES_PATIENT_DIRECTORY_DETAILS,
  APP_ROUTES_PATIENT_ENCOUNTERS,
  APP_ROUTES_PATIENT_VITALS,
  APP_ROUTES_PERMISSION,
  APP_ROUTES_PROCEDURE,
  APP_ROUTES_PRODUCT_MODULES,
  APP_ROUTES_RADIO,
  APP_ROUTES_REGISTRATION_TEMPLATE,
  APP_ROUTES_REGISTRATION_TEMPLATE_NEW,
  APP_ROUTES_SCHEDULE,
  APP_ROUTES_SHIFT,
  APP_ROUTES_STAFF,
  APP_ROUTES_SUPPLIER,
  APP_ROUTES_UNAUTHORIZE,
  APP_ROUTES_UNITS,
  APP_ROUTES_USER_VIDEO_GUIDE
} from '@data'
import { useUserRole } from '@hook'
import { lazy, Suspense, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Requisitions from '../pages/Pharmacist/Drug Inventory/Requisitions'
import { useAppSelector } from '../store/hooks'
import { hasAccessToCreateManage, hasAccessToSidebar } from '../utils'
import IntakeFormRoute from './IntakeFormRoute'
import type { RouteConfig } from './public.routes'

// Admin / shared pages
const Unauthorized = lazy(() => import('@pages/Unauthorized'))
const Staff = lazy(() => import('@admin/Staff'))
const Patient = lazy(() => import('@admin/Patient'))
const Equipment = lazy(() => import('@admin/Inventory/Equipment'))
const EquipmentProperty = lazy(() => import('@admin/Inventory/Equipment/Property'))
const Permission = lazy(() => import('@admin/Permission'))
const ProductModulesAdmin = lazy(() => import('@admin/ProductModules'))
const Department = lazy(() => import('@admin/Department'))
const Supplier = lazy(() => import('@admin/Supplier'))
const Location = lazy(() => import('@admin/Location'))
const Audit = lazy(() => import('@admin/Audit'))
const Hmo = lazy(() => import('@admin/HMO'))
const Tariffs = lazy(() => import('@admin/HMO/Tariffs'))
const Plans = lazy(() => import('@admin/HMO/Plans'))
const Imaging = lazy(() => import('@admin/Imaging'))
const Forms = lazy(() => import('@admin/Forms'))
const NewForm = lazy(() => import('@admin/Forms/NewForm'))
const ViewForm = lazy(() => import('@admin/Forms/ViewForm'))
const Procedure = lazy(() => import('@admin/Procedure'))
const Shift = lazy(() => import('@admin/Shift'))
const Schedule = lazy(() => import('@admin/Schedule'))
const Appointment = lazy(() => import('@admin/Appointment'))
const AppointmentSetup = lazy(() => import('@admin/Appointment/AppointmentSetup'))
const Drug = lazy(() => import('@admin/Drug'))
const Laboratory = lazy(() => import('@admin/Laboratory'))
const BedRequest = lazy(() => import('@admin/BedRequest'))
const Units = lazy(() => import('@admin/Units'))
const Billing = lazy(() => import('@admin/Billing'))
const Room = lazy(() => import('@admin/Units/Room'))
const Bed = lazy(() => import('@admin/Units/Bed'))
const InvoiceItems = lazy(() => import('@admin/Billing/InvoiceItems'))
const ChargeItem = lazy(() => import('@admin/ChargeItem'))
const VideoGuide = lazy(() => import('@admin/VideoGuide'))
const Facilities = lazy(() => import('@admin/Facilities'))
const Archives = lazy(() => import('@admin/Archives'))
const ClinicSummary = lazy(() => import('@pages/Reports'))
const PharmacyReports = lazy(() => import('@pages/Reports/Pharmacy'))
// User / role-specific pages
const IntakePage = lazy(() => import('@general/Intake'))
const Intake = lazy(() => import('@directory/Intake/IntakeForm'))
const DirectorySearch = lazy(() => import('@directory/DirectorySearch'))
const PatientDirectory = lazy(() => import('@directory'))
const NewIntake = lazy(() => import('@reception/NewIntake'))
const EncounterLists = lazy(() => import('@doctor/Encounter/EncounterList'))
const AssignedPatient = lazy(() => import('@doctor/AssignedPatient'))
const AdmissionOrder = lazy(() => import('@pages/AdmissionOrder'))
const Encounter = lazy(() => import('@doctor/Encounter'))
const BloodBank = lazy(() => import('@pages/BloodBank'))
const CompareVitals = lazy(() => import('@doctor/Encounter/Rounding/CompareVitals/index'))
const CompareLab = lazy(() => import('@doctor/Encounter/Rounding/CompareResults/lab'))
const CompareImaging = lazy(() => import('@doctor/Encounter/Rounding/CompareResults/imaging'))
const Allergy = lazy(() => import('@directory/Allergies'))
const PatientMAR = lazy(() => import('@/src/pages/Doctor/Encounter/M.A.R/Nurse/PatientMar'))
const MedOrder = lazy(() => import('@pharmacist/MedOrder'))
const PharmacyWorkspace = lazy(() => import('@pharmacist/PharmacyWorkspace'))
const ProductList = lazy(() => import('@pharmacist/Drug Inventory/Product List'))
const ProductDetails = lazy(() => import('../pages/Pharmacist/Drug Inventory/Product List/Product Details'))
const AddRequisition = lazy(
  () => import('../pages/Pharmacist/Drug Inventory/Requisitions/Actions/Pages/Add Requisition')
)
const Inpatient = lazy(() => import('@pharmacist/Dispense/inpatient'))
const PrescriptionFill = lazy(() => import('../pages/Pharmacist/Dispense/PrescriptionFill'))
const LabDashboard = lazy(() => import('@pages/LabTech'))
const RadiologistDashboard = lazy(() => import('@pages/Radiologist'))
const AncRegister = lazy(() => import('@pages/Antatal'))
const AncPatientMaternity = lazy(() => import('@pages/Antatal/PatientMaternityPage'))
const AncVisitForm = lazy(() => import('@pages/Antatal/VisitForm'))
const AncCard = lazy(() => import('@pages/Antatal/AncCard'))
const AncLabour = lazy(() => import('@pages/Antatal/LabourWorkspace'))
const AncPostnatalVisit = lazy(() => import('@pages/Antatal/PostnatalVisitWorkspace'))
const AncFormTemplates = lazy(() => import('@pages/Antatal/FormTemplates'))
const ObOrderSets = lazy(() => import('@pages/Antatal/OrderSets'))
const LegacyAntenatalRedirect = lazy(() => import('@pages/Antatal/LegacyAntenatalRedirect'))
const DoctorWaitlist = lazy(() => import('@doctor/waitlist'))
const NurseSchedule = lazy(() => import('../pages/Nurse/Schedule'))
const PharmacySchedule = lazy(() => import('../pages/Nurse/Schedule'))
const NurseMAR = lazy(() => import('@/src/pages/Doctor/Encounter/M.A.R/Nurse').then(m => ({ default: m.MAR })))
const DoctorMAR = lazy(() =>
  import('@/src/pages/Doctor/Encounter/M.A.R/Nurse/NurseMarbin').then(m => ({ default: m.MAR }))
)
const LabDocTemplate = lazy(() => import('@/src/pages/LabTech/DocTemplate'))
const LabNewDocTemplate = lazy(() => import('@/src/pages/LabTech/DocTemplate/AddTemplate'))
const LabDocTemplatePreview = lazy(() => import('@/src/pages/LabTech/DocTemplate/Preview'))
const RegistrationTemplateList = lazy(() => import('@/src/pages/Admin/RegistrationTemplate'))
const RegistrationTemplateEdit = lazy(() => import('@/src/pages/Admin/RegistrationTemplate/EditTemplate'))
const ImagingDocTemplate = lazy(() => import('@/src/pages/Radiologist/DocTemplate'))
const ImagingNewDocTemplate = lazy(() => import('@/src/pages/Radiologist/DocTemplate/AddTemplate'))
const ImagingDocTemplatePreview = lazy(() => import('@/src/pages/Radiologist/DocTemplate/Preview'))

function WaitlistRedirect() {
  const { isSuperAdmin } = useUserRole()
  const sidebarItems = useAppSelector(state => state.user.user?.sidebar_items) || []
  const navigate = useNavigate()

  useEffect(() => {
    if (isSuperAdmin || hasAccessToSidebar(sidebarItems, 'doctorswaitinglist')) {
      navigate(APP_ROUTES_APP_WAITLIST_DOCTORS, { replace: true })
      return
    }
    if (hasAccessToSidebar(sidebarItems, 'nurseswaitinglist')) {
      navigate(APP_ROUTES_APP_WAITLIST_NURSES, { replace: true })
      return
    }
    if (hasAccessToSidebar(sidebarItems, 'receptionistswaitinglist')) {
      navigate(APP_ROUTES_APP_WAITLIST_RECEPTIONISTS, { replace: true })
      return
    }
    navigate(APP_ROUTES_UNAUTHORIZE, { replace: true })
  }, [isSuperAdmin, navigate, sidebarItems])

  return <div className="flex flex-1 items-center justify-center p-8">Loading…</div>
}

function ScheduleRoute() {
  const { isPharmacist } = useUserRole()
  return (
    <Suspense fallback={<div className="flex flex-1 items-center justify-center p-8">Loading…</div>}>
      {isPharmacist ? <PharmacySchedule /> : <NurseSchedule />}
    </Suspense>
  )
}

function MarRoute() {
  const { isNurse } = useUserRole()
  return (
    <Suspense fallback={<div className="flex flex-1 items-center justify-center p-8">Loading…</div>}>
      {isNurse ? <NurseMAR /> : <NurseMAR />}
    </Suspense>
  )
}

function ScheduleOrManagementRoute() {
  const { user } = useAppSelector(state => state.user)
  const permissions = user?.permissions
  return (
    <Suspense fallback={<div className="flex flex-1 items-center justify-center p-8">Loading…</div>}>
      {hasAccessToCreateManage(permissions, 'schedule') ? <Schedule /> : <ScheduleRoute />}
    </Suspense>
  )
}

const adminRoutes: RouteConfig[] = [
  { element: <Unauthorized />, path: APP_ROUTES_UNAUTHORIZE },
  { element: <VideoGuide />, path: APP_ROUTES_USER_VIDEO_GUIDE },
  { element: <Staff />, path: APP_ROUTES_STAFF, permissionKey: 'staff' },
  { element: <Patient />, path: APP_ROUTES_PATIENT, permissionKey: 'patient' },
  { element: <Equipment />, path: APP_ROUTES_INVENTORY_EQUIPMENT, permissionKey: 'equipment' },
  { element: <EquipmentProperty />, path: APP_ROUTES_INVENTORY_EQUIPMENT_DETAIL },
  { element: <Permission />, path: APP_ROUTES_PERMISSION, permissionKey: 'role' },
  { element: <ProductModulesAdmin />, path: APP_ROUTES_PRODUCT_MODULES },
  { element: <Department />, path: APP_ROUTES_DEPARTMENT, permissionKey: 'department' },
  { element: <Supplier />, path: APP_ROUTES_SUPPLIER, permissionKey: 'supplier' },
  { element: <Location />, path: APP_ROUTES_LOCATION, permissionKey: 'location' },
  { element: <Audit />, path: APP_ROUTES_AUDIT, permissionKey: 'audit log' },
  { element: <Hmo />, path: APP_ROUTES_HMO, permissionKey: 'hmos' },
  { element: <Plans />, path: APP_ROUTES_HMO_PLAN, permissionKey: 'hmos' },
  { element: <Tariffs />, path: APP_ROUTES_HMO_PLAN_TARIFF, permissionKey: 'hmos' },
  { element: <Shift />, path: APP_ROUTES_SHIFT, permissionKey: 'shift' },
  { element: <ScheduleOrManagementRoute />, path: APP_ROUTES_SCHEDULE, permissionKey: 'schedule' },
  { element: <Appointment />, path: APP_ROUTES_APPOINTMENT, permissionKey: 'appointment' },
  { element: <AppointmentSetup />, path: APP_ROUTES_APPOINTMENT_SETUP, permissionKey: 'appointment' },
  { element: <Drug />, path: APP_ROUTES_DRUG, permissionKey: 'drug' },
  { element: <Procedure />, path: APP_ROUTES_PROCEDURE, permissionKey: 'procedure' },
  { element: <Laboratory />, path: APP_ROUTES_LABORATORY, permissionKey: 'lab_test' },
  { element: <BedRequest />, path: APP_ROUTES_BED_REQUEST, permissionKey: 'bed request' },
  { element: <Imaging />, path: APP_ROUTES_IMAGING, permissionKey: 'imaging' },
  { element: <Forms />, path: APP_ROUTES_FORMS, permissionKey: 'document template' },
  { element: <NewForm />, path: APP_ROUTES_ADD_FORMS, permissionKey: 'document template' },
  { element: <ViewForm />, path: `${APP_ROUTES_FORMS}/:id`, permissionKey: 'document template' },
  { element: <Units />, path: APP_ROUTES_UNITS, permissionKey: 'unit' },
  { element: <Room />, path: APP_ROUTES_DEPARTMENT_ROOM },
  { element: <Bed />, path: APP_ROUTES_BED },
  { element: <Billing />, path: APP_ROUTES_BILLING, permissionKey: 'billing' },
  { element: <InvoiceItems />, path: `${APP_ROUTES_BILLING}/:id`, permissionKey: 'billing' },
  { element: <ChargeItem />, path: APP_ROUTES_CHARGE_ITEMS, permissionKey: 'charge item' },
  {
    element: <ClinicSummary />,
    path: APP_ROUTES_APP_REPORTS,
    permissionKey: 'reports'
  },
  {
    element: <PharmacyReports />,
    path: APP_ROUTES_APP_PHARMACY_REPORTS,
    permissionKey: 'pharmacy reports'
  },
  { element: <Facilities />, path: APP_ROUTES_FACILITY, permissionKey: 'facility' },
  { element: <Archives />, path: APP_ROUTES_ARCHIVES, permissionKey: 'archives' }
]

const userOnlyRoutes: RouteConfig[] = [
  {
    element: <WaitlistRedirect />,
    path: APP_ROUTES_APP_WAITLIST,
    permissionKey: ['doctorswaitinglist', 'nurseswaitinglist', 'receptionistswaitinglist']
  },
  {
    element: <DoctorWaitlist />,
    path: APP_ROUTES_APP_WAITLIST_DOCTORS,
    permissionKey: 'doctorswaitinglist'
  },
  {
    element: <IntakePage title="Waitlist" />,
    path: APP_ROUTES_APP_WAITLIST_NURSES,
    permissionKey: 'nurseswaitinglist'
  },
  {
    element: <IntakePage />,
    path: APP_ROUTES_APP_WAITLIST_RECEPTIONISTS,
    permissionKey: 'receptionistswaitinglist'
  },
  { element: <IntakePage />, path: APP_ROUTES_APP_INTAKE, permissionKey: ['receptionistsintake', 'nursesintake'] },
  {
    element: (
      <IntakeFormRoute>
        <Intake />
      </IntakeFormRoute>
    ),
    path: APP_ROUTES_APP_PATIENT_CHART_INTAKE_ID
  },
  {
    element: (
      <IntakeFormRoute>
        <Intake />
      </IntakeFormRoute>
    ),
    path: APP_ROUTES_APP_PATIENT_CHART_INTAKE
  },
  { element: <NewIntake />, path: APP_ROUTES_APP_INTAKE_NEW },
  { element: <DirectorySearch />, path: APP_ROUTES_PATIENT_DIRECTORY, permissionKey: 'patient' },
  { element: <PatientDirectory />, path: APP_ROUTES_PATIENT_DIRECTORY_DETAILS },
  { element: <MarRoute />, path: '/mar', permissionKey: 'mar' },
  { element: <PatientMAR />, path: APP_ROUTES_APP_PATIENT_MAR },
  { element: <MedOrder />, path: APP_ROUTES_APP_MED_ORDER, permissionKey: 'med order' },
  {
    element: <PharmacyWorkspace />,
    path: APP_ROUTES_APP_PHARMACY_WORKSPACE,
    permissionKey: ['med order', 'drug inventory', 'dispensings', 'pharmacist_requisition', 'nurse_requisition']
  },
  { element: <ProductList />, path: APP_ROUTES_APP_DRUG_INVENTORY, permissionKey: 'drug inventory' },
  { element: <ProductDetails />, path: APP_ROUTES_APP_PRODUCT_DETAILS, permissionKey: 'drug inventory' },
  { element: <Inpatient />, path: APP_ROUTES_APP_DISPENSE_INPATIENT, permissionKey: 'dispensings' },
  {
    element: <Requisitions variant="pharmacist" />,
    path: APP_ROUTES_APP_PHARMACIST_REQUISITIONS,
    permissionKey: 'pharmacist_requisition'
  },
  {
    element: <Requisitions variant="nurse" />,
    path: APP_ROUTES_APP_NURSE_REQUISITIONS,
    permissionKey: 'nurse_requisition'
  },
  {
    element: <AddRequisition variant="pharmacist" />,
    path: APP_ROUTES_APP_PHARMACIST_REQUISITIONS_NEW,
    permissionKey: 'pharmacist_requisition'
  },
  {
    element: <AddRequisition variant="nurse" />,
    path: APP_ROUTES_APP_REQUISITIONS_NEW,
    permissionKey: 'nurse_requisition'
  },
  {
    element: <AddRequisition variant="pharmacist" />,
    path: `${APP_ROUTES_APP_PHARMACIST_REQUISITIONS}/:id/edit`,
    permissionKey: 'pharmacist_requisition'
  },
  {
    element: <AddRequisition variant="nurse" />,
    path: `${APP_ROUTES_APP_NURSE_REQUISITIONS}/:id/edit`,
    permissionKey: 'nurse_requisition'
  },
  {
    element: <PrescriptionFill prescriptionType="outpatient" />,
    path: APP_ROUTES_APP_DISPENSE_OUTPATIENT,
    permissionKey: 'dispensings'
  },
  { element: <Forms />, path: APP_ROUTES_APP_DOCUMENT_TEMPLATE, permissionKey: 'document template' },
  { element: <ViewForm />, path: `${APP_ROUTES_APP_DOCUMENT_TEMPLATE}/:id`, permissionKey: 'document template' },
  { element: <NewForm />, path: APP_ROUTES_APP_DOCUMENT_TEMPLATE_NEW, permissionKey: 'document template' },
  { element: <EncounterLists />, path: APP_ROUTES_PATIENT_ENCOUNTERS, permissionKey: 'encounter' },
  { element: <EncounterLists />, path: APP_ROUTES_APP_ENCOUNTERS, permissionKey: 'encounter' },
  { element: <BloodBank />, path: APP_ROUTES_BLOOD_BANK, permissionKey: 'encounter' },
  { element: <BloodBank />, path: `${APP_ROUTES_BLOOD_BANK}/:encounterId`, permissionKey: 'encounter' },
  { element: <CompareVitals />, path: APP_ROUTES_PATIENT_VITALS, permissionKey: 'encounter' },
  { element: <AssignedPatient />, path: APP_ROUTES_APP_ASSIGNED, permissionKey: 'encounter' },
  { element: <Encounter />, path: '/patient-chart/:patientId/encounter/:encounterId', permissionKey: 'encounter' },
  {
    element: <CompareLab />,
    path: '/patient-chart/:patientId/encounter/:encounterId/rounding/lab',
    permissionKey: 'encounter'
  },
  {
    element: <CompareImaging />,
    path: '/patient-chart/:patientId/encounter/:encounterId/rounding/imaging',
    permissionKey: 'imaging'
  },
  { element: <AdmissionOrder />, path: '/patient-chart/:patientId/admission', permissionKey: 'encounter' },
  { element: <AdmissionOrder />, path: APP_ROUTES_APP_ADMISSION, permissionKey: 'pending admits' },
  { element: <Allergy />, path: APP_ROUTES_ALLERGY },
  { element: <LabDashboard />, path: APP_ROUTES_LAB_TECH, permissionKey: 'laboratory' },
  { element: <RadiologistDashboard />, path: APP_ROUTES_RADIO, permissionKey: 'radiology' },
  { element: <AncRegister />, path: APP_ROUTES_ANC, permissionKey: 'antenatal' },
  { element: <AncPatientMaternity />, path: APP_ROUTES_ANC_PATIENT, permissionKey: 'antenatal' },
  { element: <AncVisitForm />, path: APP_ROUTES_ANC_VISIT, permissionKey: 'antenatal' },
  { element: <AncCard />, path: APP_ROUTES_ANC_CARD, permissionKey: 'antenatal' },
  { element: <AncLabour />, path: APP_ROUTES_ANC_LABOUR, permissionKey: 'antenatal' },
  { element: <AncPostnatalVisit />, path: APP_ROUTES_ANC_PN_VISIT, permissionKey: 'antenatal' },
  { element: <AncFormTemplates />, path: APP_ROUTES_ANC_FORM_TEMPLATES, permissionKey: 'antenatal' },
  { element: <ObOrderSets />, path: APP_ROUTES_OB_ORDER_SETS, permissionKey: 'antenatal' },
  { element: <LegacyAntenatalRedirect />, path: '/antenatal', permissionKey: 'antenatal' },
  { element: <LegacyAntenatalRedirect />, path: '/antenatal/*', permissionKey: 'antenatal' },
  { element: <LabDocTemplate />, path: APP_ROUTES_LABORATORY_DOC_TEMPLATE },
  {
    element: <LabNewDocTemplate />,
    path: APP_ROUTES_LABORATORY_DOC_TEMPLATE_NEW
  },
  {
    element: <LabDocTemplatePreview />,
    path: APP_ROUTES_LABORATORY_DOC_TEMPLATE_PREVIEW
  },
  {
    element: <RegistrationTemplateList />,
    path: APP_ROUTES_REGISTRATION_TEMPLATE,
    permissionKey: 'registration template'
  },
  {
    element: <RegistrationTemplateEdit />,
    path: APP_ROUTES_REGISTRATION_TEMPLATE_NEW,
    permissionKey: 'registration template'
  },
  { element: <ImagingDocTemplate />, path: APP_ROUTES_IMAGING_DOC_TEMPLATE },
  {
    element: <ImagingNewDocTemplate />,
    path: APP_ROUTES_IMAGING_DOC_TEMPLATE_NEW
  },
  {
    element: <ImagingDocTemplatePreview />,
    path: APP_ROUTES_IMAGING_DOC_TEMPLATE_PREVIEW
  }
]

const adminPaths = new Set(adminRoutes.map(r => r.path))
export const appRoutes: RouteConfig[] = [...adminRoutes, ...userOnlyRoutes.filter(r => !adminPaths.has(r.path))]
